import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const THEME = {
  bg: '#f5f0e6',
  text: '#6b3a2a',
  textMuted: '#8b5e4b',
  accent: '#7b4f2f',
  divider: '#d4c4b0',
  panel: '#fffdf8',
  border: '#e8dfd2',
};

const SLOTS_PER_PAGE = 9;

const WEEKDAY_ZH = ['日', '一', '二', '三', '四', '五', '六'];

function authHeaders() {
  try {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function sliceHm(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2}:\d{2})/);
  return m ? m[1] : s.slice(0, 5);
}

function formatDateWithWeekday(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(ymd || '').trim();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const wd = WEEKDAY_ZH[d.getDay()] || '';
  return `${Number(m[2])}/${Number(m[3])}(${wd})`;
}

function formatTimeRange(card) {
  const datePart = formatDateWithWeekday(card.date);
  const start = sliceHm(card.time);
  const end = sliceHm(card.endTime);
  if (datePart && start && end) return `${datePart} ${start} ~ ${end}`;
  if (datePart && start) return `${datePart} ${start}`;
  return start || datePart || '';
}

function activityToCard(act) {
  return {
    activityId: act.id,
    title: act.name || '',
    subtitle: act.organizer || '',
    date: act.activity_date || '',
    time: sliceHm(act.activity_time),
    endTime: sliceHm(act.end_time),
    location: act.venue || '',
    price: act.consumption_type || '',
  };
}

function fontScale(size) {
  if (size === 'small') return 0.85;
  if (size === 'large') return 1.15;
  return 1;
}

function PreviewCard({ card, scale, onClick, selected }) {
  const fs = fontScale(scale);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        aspectRatio: '1 / 1',
        width: '100%',
        border: 'none',
        borderRight: `1px solid ${THEME.divider}`,
        borderBottom: `1px solid ${THEME.divider}`,
        borderRadius: 0,
        background: THEME.bg,
        padding: 8,
        textAlign: 'center',
        cursor: card ? 'pointer' : 'default',
        boxShadow: 'none',
        outline: selected ? `2px solid ${THEME.accent}` : 'none',
        outlineOffset: -2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        fontFamily: '"jf-openhuninn", "PingFang TC", "Microsoft JhengHei", sans-serif',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {card ? (
        <>
          {/* 上半部：標題區 */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              color: THEME.text,
              fontSize: Math.round(12 * fs),
              fontWeight: 700,
              lineHeight: 1.3,
              marginBottom: 3,
              wordBreak: 'break-word',
              overflow: 'hidden',
              maxHeight: '2.6em',
            }}
            >
              {card.title || '（活動名稱）'}
            </div>
            {card.subtitle ? (
              <div style={{ 
                color: THEME.textMuted, 
                fontSize: Math.round(8 * fs), 
                marginBottom: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {card.subtitle}
              </div>
            ) : null}
            <div style={{ borderTop: `1px dashed ${THEME.divider}`, margin: '4px auto', width: '90%' }} />
          </div>
          
          {/* 下半部：資訊列 */}
          <div style={{ 
            textAlign: 'left', 
            fontSize: Math.round(8 * fs), 
            color: THEME.text, 
            lineHeight: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            {formatTimeRange(card) ? <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>◷ {formatTimeRange(card)}</div> : null}
            {card.location ? <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⌖ {card.location}</div> : null}
            {card.price ? <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>$ {card.price}</div> : null}
          </div>
        </>
      ) : null}
    </button>
  );
}

function A4PreviewPage({ cards, fontSize, activeCardId, onCardClick }) {
  const slots = useMemo(() => {
    const pageCards = cards.slice(0, SLOTS_PER_PAGE);
    const out = [...pageCards];
    while (out.length < SLOTS_PER_PAGE) out.push(null);
    return out;
  }, [cards]);

  // A4 比例 210:297，計算適合放 3x3 正方形的容器
  // 取較小維度來確保正方形能放入
  const a4Ratio = 210 / 297;
  
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 420,
        aspectRatio: '210 / 297',
        margin: '0 auto',
        background: '#fff',
        border: `1px solid ${THEME.divider}`,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 3x3 正方形網格容器 */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
          gap: 0,
        }}
      >
        {slots.map((card, idx) => (
          <PreviewCard
            key={card ? card.activityId : `empty-${idx}`}
            card={card}
            scale={fontSize}
            selected={card && activeCardId === card.activityId}
            onClick={card ? () => onCardClick(card) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function CardEditModal({ card, onClose, onSave }) {
  const [draft, setDraft] = useState(card);
  useEffect(() => { setDraft(card); }, [card]);

  if (!card) return null;

  const field = (label, key, placeholder = '') => (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</span>
      <input
        value={draft[key] || ''}
        placeholder={placeholder}
        onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 10px',
          borderRadius: 8,
          border: '1px solid #d1d5db',
          fontSize: 14,
        }}
      />
    </label>
  );

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(480px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          background: '#fff',
          borderRadius: 14,
          padding: 20,
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>編輯卡片</h3>
        {field('標題', 'title')}
        {field('副標（主辦）', 'subtitle')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {field('日期', 'date', 'YYYY-MM-DD')}
          {field('開始', 'time', '19:00')}
          {field('結束', 'endTime', '21:30')}
        </div>
        {field('地點', 'location')}
        {field('費用／入場方式', 'price')}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff' }}>取消</button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: THEME.accent, color: '#fff', fontWeight: 700 }}
          >
            儲存
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WeeklyActivityCardPrintPanel({
  activities = [],
  loading = false,
  btnStyle = {},
  btnPrimary = {},
  waNarrow = false,
}) {
  const [search, setSearch] = useState('');
  const [cards, setCards] = useState([]);
  const [fontSize, setFontSize] = useState('medium');
  const [previewUrl, setPreviewUrl] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  const [activeCardId, setActiveCardId] = useState(null);

  const selectedIds = useMemo(() => new Set(cards.map((c) => c.activityId)), [cards]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = (activities || []).filter((a) => a.status !== 'draft');
    if (!q) return base;
    return base.filter((a) => [a.name, a.organizer, a.venue, a.activity_date, a.consumption_type].join(' ').toLowerCase().includes(q));
  }, [activities, search]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const toggleActivity = (act) => {
    setMsg('');
    if (selectedIds.has(act.id)) {
      setCards((prev) => prev.filter((c) => c.activityId !== act.id));
      if (activeCardId === act.id) setActiveCardId(null);
      return;
    }
    if (cards.length >= SLOTS_PER_PAGE) {
      setMsg(`每頁最多 ${SLOTS_PER_PAGE} 張卡片`);
      return;
    }
    setCards((prev) => [...prev, activityToCard(act)]);
  };

  const updateCard = (activityId, patch) => {
    setCards((prev) => prev.map((c) => (c.activityId === activityId ? { ...c, ...patch } : c)));
  };

  const runPreview = useCallback(async () => {
    if (!cards.length) return;
    setBusy('preview');
    setMsg('');
    try {
      const { data } = await axios.post('/api/admin/activity-cards/preview', { cards, fontSize }, {
        headers: authHeaders(),
        timeout: 120000,
      });
      if (!data?.pdf) throw new Error('未取得預覽 PDF');
      const bytes = Uint8Array.from(atob(data.pdf), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      setMsg(`預覽失敗：${e?.response?.data?.message || e?.message || '未知錯誤'}`);
    } finally {
      setBusy('');
    }
  }, [cards, fontSize]);

  const runPrint = async () => {
    if (!cards.length || busy) return;
    setBusy('print');
    setMsg('');
    try {
      const { data } = await axios.post('/api/admin/activity-cards/print-direct', { cards, fontSize }, {
        headers: authHeaders(),
        timeout: 120000,
      });
      setMsg(data?.printHint || `已送印 ${data?.count || cards.length} 張活動卡片（彩色）`);
    } catch (e) {
      setMsg(`列印失敗：${e?.response?.data?.message || e?.message || '未知錯誤'}`);
    } finally {
      setBusy('');
    }
  };

  const panelStyle = {
    border: `1px solid ${THEME.border}`,
    borderRadius: 14,
    background: THEME.panel,
    padding: 14,
    minHeight: 320,
  };

  const gridCols = waNarrow ? '1fr' : 'minmax(240px, 1fr) minmax(360px, 1.6fr)';

  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>活動卡片列印</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>左側點選活動，右側 A4 預覽點卡片編輯。</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>字級</span>
          {['small', 'medium', 'large'].map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => setFontSize(size)}
              style={{
                ...btnStyle,
                ...(fontSize === size ? btnPrimary : {}),
                padding: '6px 12px',
                fontSize: 13,
              }}
            >
              {size === 'small' ? '小' : size === 'large' ? '大' : '中'}
            </button>
          ))}
          <button type="button" disabled={!cards.length || !!busy} onClick={runPreview} style={{ ...btnStyle, padding: '8px 14px' }}>
            {busy === 'preview' ? '產生中…' : '預覽 PDF'}
          </button>
          <button type="button" disabled={!cards.length || !!busy} onClick={runPrint} style={{ ...btnPrimary, padding: '8px 16px' }}>
            {busy === 'print' ? '送印中…' : '彩色列印'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 14, alignItems: 'start' }}>
        <div style={panelStyle}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>活動</div>
          <input
            type="search"
            placeholder="搜尋名稱、主辦、場地…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 10 }}
          />
          <div style={{ maxHeight: waNarrow ? 280 : 620, overflowY: 'auto', display: 'grid', gap: 8 }}>
            {loading ? <div style={{ color: '#9ca3af', fontSize: 13 }}>載入中…</div> : null}
            {!loading && filtered.length === 0 ? <div style={{ color: '#9ca3af', fontSize: 13 }}>沒有符合的活動</div> : null}
            {filtered.map((act) => {
              const picked = selectedIds.has(act.id);
              return (
                <button
                  key={act.id}
                  type="button"
                  onClick={() => toggleActivity(act)}
                  style={{
                    textAlign: 'left',
                    border: picked ? `2px solid ${THEME.accent}` : '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: picked ? '#fff7ed' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{act.name || '（未命名）'}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {act.activity_date}
                    {' '}
                    {sliceHm(act.activity_time)}
                    {act.venue ? ` · ${act.venue}` : ''}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={panelStyle}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>A4 預覽（點卡片編輯）</div>
          {cards.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '60px 0', textAlign: 'center' }}>從左側點選活動</div>
          ) : (
            <A4PreviewPage
              cards={cards}
              fontSize={fontSize}
              activeCardId={activeCardId}
              onCardClick={(card) => {
                setActiveCardId(card.activityId);
                setEditingCard(card);
              }}
            />
          )}
          {previewUrl ? (
            <iframe
              title="活動卡片 PDF"
              src={previewUrl}
              style={{
                width: '100%',
                maxWidth: 480,
                height: waNarrow ? 400 : 520,
                margin: '14px auto 0',
                display: 'block',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                background: '#f9fafb',
              }}
            />
          ) : null}
        </div>
      </div>

      {msg ? (
        <pre style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#f3f4f6', fontSize: 13, whiteSpace: 'pre-wrap' }}>{msg}</pre>
      ) : null}

      <CardEditModal
        card={editingCard}
        onClose={() => setEditingCard(null)}
        onSave={(draft) => {
          updateCard(draft.activityId, draft);
          setEditingCard(null);
        }}
      />
    </section>
  );
}
